import React, { useEffect, useState } from "react";
import axios from "axios";
import { Radio, Store, Wifi } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Live "who's covering right now" pill row.
 *
 * Renders one glowing-green chip per active shift so every admin
 * sees at a glance who's online and who's in the boutique without
 * opening CRM. Refreshes every 30s.
 *
 * • Sorted online-first, then by start time.
 * • Own chip is highlighted with a gold ring.
 * • Empty state (nobody on shift) shows a soft gray notice.
 */
export default function OnShiftBar({ myId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/shift/active`,
                                 { withCredentials: true });
      setItems(r.data.items || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  if (loading) return null;

  return (
    <div data-testid="on-shift-bar"
      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                background: items.length > 0 ? "rgba(22,163,74,0.06)" : "transparent",
                border: items.length > 0 ? "1px solid rgba(22,163,74,0.25)" : "1px dashed #e5e7eb",
                padding: "8px 12px", borderRadius: 999, marginBottom: 4,
                maxWidth: "100%", overflowX: "auto" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6,
                     fontFamily: "Manrope", fontSize: 11, fontWeight: 700,
                     letterSpacing: "0.12em", textTransform: "uppercase",
                     color: items.length > 0 ? "#15803d" : "#9ca3af",
                     whiteSpace: "nowrap" }}>
        <Radio size={12} className={items.length > 0 ? "shift-pulse" : ""} />
        На смене · {items.length}
      </span>
      {items.length === 0 ? (
        <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#94a3b8" }}>
          сейчас никто не на смене
        </span>
      ) : (
        items.map((s) => {
          const isMe = myId && s.manager_id === myId;
          const online = s.mode === "online";
          const paused = s.available_for_leads === false;
          const goalRev = Number(s.goal?.revenue || 0);
          const actual = Number(s.auto_stats?.revenue || 0);
          const pct = goalRev > 0 ? Math.min(999, Math.round((actual / goalRev) * 100)) : null;
          return (
            <div key={s.manager_id}
              data-testid={`on-shift-chip-${s.manager_id}`}
              title={paused
                ? `${s.manager_name} · ПАУЗА (не принимает лиды)`
                : (goalRev > 0
                    ? `${s.manager_name} · план ${goalRev.toLocaleString("ru-RU")} ₸ · факт ${actual.toLocaleString("ru-RU")} ₸ (${pct}%)`
                    : `${s.manager_name} · ${online ? "онлайн" : "в бутике"}`)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6,
                       background: "white",
                       border: isMe ? "1.5px solid #C5A059" : (paused ? "1px solid rgba(245,158,11,0.55)" : "1px solid rgba(22,163,74,0.35)"),
                       boxShadow: isMe ? "0 0 0 2px rgba(197,160,89,0.15)" : (paused ? "0 0 0 2px rgba(245,158,11,0.12)" : "0 0 0 2px rgba(22,163,74,0.08)"),
                       borderRadius: 999, padding: "4px 10px",
                       fontFamily: "Manrope", fontSize: 12, color: "#0A0A0A",
                       whiteSpace: "nowrap", opacity: paused ? 0.85 : 1 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%",
                             background: paused ? "#f59e0b" : "#16a34a",
                             boxShadow: paused ? "0 0 6px rgba(245,158,11,0.65)" : "0 0 6px rgba(22,163,74,0.65)" }}
                    className={paused ? "" : "shift-dot-pulse"} />
              {online
                ? <Wifi size={11} style={{ color: paused ? "#f59e0b" : "#16a34a" }} />
                : <Store size={11} style={{ color: "#B45309" }} />}
              <span style={{ fontWeight: isMe ? 700 : 500 }}>
                {s.manager_name}{isMe && " · вы"}{paused && " (пауза)"}
              </span>
              {pct !== null && (
                <span style={{ marginLeft: 4, padding: "1px 7px", borderRadius: 999,
                                background: pct >= 100 ? "#dcfce7" : pct >= 60 ? "#fef3c7" : "#fee2e2",
                                color:      pct >= 100 ? "#166534" : pct >= 60 ? "#92400e" : "#991b1b",
                                fontSize: 10.5, fontWeight: 800 }}>
                  {pct}%
                </span>
              )}
            </div>
          );
        })
      )}
      <style>{`
        @keyframes shiftPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.15); }
        }
        .shift-pulse { animation: shiftPulse 2.2s ease-in-out infinite; }
        .shift-dot-pulse { animation: shiftPulse 1.8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
